import type {
  Deliverable,
  DeliverableStage,
  DeliverableStatus,
  DeliverableType,
} from '../types/portal'
import { DELIVERABLE_STAGES, REVIEWABLE } from '../types/portal'

/** Client-facing heading for each stage, in the order the client sees them. */
export const CLIENT_STAGE_ORDER: DeliverableStage[] = DELIVERABLE_STAGES

export function stageHeading(stage: DeliverableStage): string {
  return stage === 'Review' ? 'In Review' : stage
}

/**
 * The production stage a status implies. Staff only ever set the status, so
 * this is the single source of truth for the stage — Portal and client both
 * read it, and they can never disagree.
 */
const STAGE_BY_STATUS: Record<DeliverableStatus, DeliverableStage> = {
  'Not Started': 'Pre-Production',
  'In Production': 'Production',
  Editing: 'Post-Production',
  'Ready for Review': 'Review',
  'Changes Requested': 'Review',
  Approved: 'Final Delivery',
  'Ready for Download': 'Final Delivery',
  Delivered: 'Final Delivery',
}

export function deliverableStage(status: DeliverableStatus): DeliverableStage {
  return STAGE_BY_STATUS[status] ?? 'Pre-Production'
}

/** True once a version number is meaningful — Review onwards. */
export function isVersioned(status: DeliverableStatus): boolean {
  const stage = deliverableStage(status)
  return stage === 'Review' || stage === 'Final Delivery'
}

const PLURAL: Record<DeliverableType, [string, string]> = {
  Video: ['Video', 'Videos'],
  Audio: ['Audio', 'Audio Files'],
  Photography: ['Image', 'Images'],
  Document: ['Document', 'Documents'],
  Graphics: ['Graphic', 'Graphics'],
  Transcript: ['Transcript', 'Transcripts'],
  Folder: ['Folder', 'Folders'],
  Other: ['Item', 'Items'],
}

export function typeLabel(type: DeliverableType, quantity: string): string {
  const count = Number(quantity)
  const [one, many] = PLURAL[type]
  if (!quantity.trim()) return many
  return `${quantity} ${Number.isFinite(count) && count === 1 ? one : many}`
}

/** "8 Videos · V3" — the small line under a deliverable name. */
export function metaLabel(deliverable: Deliverable): string {
  return [typeLabel(deliverable.type, deliverable.quantity), deliverable.version]
    .filter(Boolean)
    .join(' · ')
}

export function fileAction(deliverable: Deliverable): 'Download' | 'Open folder' {
  if (deliverable.type === 'Folder') return 'Open folder'
  if (deliverable.driveUrl.includes('/folders/')) return 'Open folder'
  return Number(deliverable.quantity) > 1 ? 'Open folder' : 'Download'
}

export function isReviewable(status: DeliverableStatus): boolean {
  return REVIEWABLE.includes(status)
}

export function clientStatusLabel(status: DeliverableStatus): string {
  switch (status) {
    case 'Not Started':
      return 'NOT STARTED'
    case 'In Production':
      return 'IN PRODUCTION'
    case 'Editing':
      return 'IN EDIT'
    case 'Ready for Review':
      return 'READY FOR REVIEW'
    case 'Changes Requested':
      return 'CHANGES REQUESTED'
    case 'Approved':
      return 'APPROVED'
    case 'Ready for Download':
      return 'READY TO DOWNLOAD'
    case 'Delivered':
      return 'DELIVERED'
  }
}

export function canDownload(status: DeliverableStatus): boolean {
  return status === 'Approved' || status === 'Ready for Download' || status === 'Delivered'
}
